import React, {useEffect, useState} from 'react';
import {IonContent, IonModal, IonPage} from '@ionic/react';
import {useHistory} from 'react-router-dom';
import {ApiService} from "../api/ApiService";
import {toastStore} from "../components/AppToast";
import {Trainee} from "../api/model/Trainee";
import LoadDetailHeader, {useTransparentHeaderContrast} from '../components/LoadDetailHeader';
import { useLogPageVisit } from '../utils/logPageVisit';
import {
    ChevronLeft,
    ChevronRight,
    PlusIcon,
    SeatedIcon,
} from '../components/icons';

const ReleaseTrainee: React.FC = () => {
    useLogPageVisit('LP Release Trainee', 'User viewed the Release Trainee page');
    const history = useHistory();
    const {onSheet, onIonScroll} = useTransparentHeaderContrast();
    const [mounted, setMounted] = useState(false);

    const today = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)

    const [showModal, setShowModal] = useState(false);
    const [noTrainees, setNoTrainees] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasTrainee, setHasTrainee] = useState(false);
    const [days, setDays] = useState<number>(0);
    const [submitType, setSubmitType] = useState<'pass' | 'fail'>("pass");
    const [trainees, setTrainees] = useState<Trainee[]>([]);
    const [trainee, setTrainee] = useState<Trainee>({
        currentCourseDay: 0,
        traineeCode: '',
        traineeName: '',
        currentCourseId: 0,
        dateCreated: '',
        dmCode: '',
        passed: 0,
        trainerCode: '',
        trainerName: '',
        released: 0,
        seatedDate: ''
    });

    let currentDate = today.toISOString().split('T')[0]

    useEffect(() => {
      const id = requestAnimationFrame(() => {
        setTimeout(() => setMounted(true), 30);
      });
      return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        ApiService.getTrainees('releaseTrainee', {released: 0}).then(it => {
            setNoTrainees(!it.length);
            setTrainees(it);
        });
    }, []);

    const getTraineeProfile = (traineeCode: string) => {
        ApiService.getTraineeProfile('releaseTrainee', traineeCode).then(it => {
            setTrainee(it);
            setDays(it.currentCourseDay);
            setHasTrainee(true);
        });
    }

    const handleSubmit = () => {
        if (agreed) {
            releaseTrainee();
        } else {
            toastStore.getState().setToast('Please select the checkbox for the agreement');
        }
    }

    const releaseTrainee = () => {
        setLoading(true);
        ApiService.traineeRelease('releaseTrainee', {
            traineeCode: trainee.traineeCode,
            releasedDate: currentDate,
            trainerCode: trainee.trainerCode,
            currentCourseDay: trainee.currentCourseDay,
            passed: submitType === "fail" ? 0 : 1,
            failureReason: ''
        }).then(() => {
            setShowModal(true);
        }).finally(() => {
            setLoading(false);
        });
    }

    const goBack = () => {
      if (history.length > 1) history.goBack();
      else history.replace('/Home');
    };

    const agreementText = submitType === 'pass'
        ? "As a duly appointed driver trainer for Western Express, Inc., I do hereby release my student for final written and driving tests. To the best of my knowledge my student has completed training. I have completed all of my required responsibilities in the training of my student and it is my firm belief that he or she is able to perform all tasks associated with the safe and successful operations of a commercial motor vehicle. I believe my student will be an asset to the company and perform their job related duties in a safe, courteous, and responsible manner."
        : "As a duly appointed driver trainer for Western Express, Inc., I DO NOT feel that my Trainee is capable of performing his job related duties in a safe and professional manner. Despite my best efforts I have been unable to instill or observe the necessary skills or work ethics required to succeed safely and professionally as an driver for Western Express. I have detailed their shortcomings in the training manual and below and they are aware of my comments and decision to recommend a company evaluation of their skills without my granting a release from training. I have found the following general problems with their overall performance.";

    return (
        <IonPage>
            <LoadDetailHeader title="Release Trainee" onBack={goBack} onSheet={onSheet} />

            <IonContent
              fullscreen
              className="design-a-content design-a-content--light"
              scrollEvents={true}
              onIonScroll={onIonScroll}
            >
              <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
                <div className="home-hero advance-hero">
                  <div className="advance-hero__eyebrow">Training</div>
                  <div className="advance-hero__title home-display">
                    {hasTrainee ? (trainee.traineeCode || 'Trainee') : 'Release Trainee'}
                  </div>
                  <div className="advance-hero__meta">
                    {hasTrainee
                      ? `Day ${trainee.currentCourseDay} · ${submitType === 'pass' ? 'Pass' : 'Fail'} release`
                      : noTrainees
                        ? 'No active trainees to release'
                        : `${trainees.length} trainee${trainees.length === 1 ? '' : 's'} available`}
                  </div>
                </div>

                <div className="home-sheet advance-sheet">
                  {hasTrainee ? (
                    <>
                      <button
                        type="button"
                        className="home-action home-action--tint"
                        onClick={() => setHasTrainee(false)}
                      >
                        <span className="home-action__icon">
                          <ChevronLeft color="#1a1a1a" size={22} />
                        </span>
                        <span className="home-action__label">Trainees</span>
                      </button>

                      <div className="home-card training-meta-card">
                        <div className="training-meta-card__row">
                          <span className="training-meta-card__label">Today's Date</span>
                          <span className="training-meta-card__value">{currentDate}</span>
                        </div>
                        <div className="training-meta-card__divider" />
                        <div className="training-meta-card__row">
                          <span className="training-meta-card__label">Trainee</span>
                          <span className="training-meta-card__value">
                            {trainee.traineeName || trainee.traineeCode}
                          </span>
                        </div>
                      </div>

                      <div className="home-section-label">Outcome</div>
                      <div className="brokerage-segment">
                        <button
                          type="button"
                          className={`brokerage-segment__btn${submitType === 'pass' ? ' is-active' : ''}`}
                          onClick={() => setSubmitType('pass')}
                        >
                          <span className="brokerage-segment__label">Pass</span>
                        </button>
                        <button
                          type="button"
                          className={`brokerage-segment__btn${submitType === 'fail' ? ' is-active' : ''}`}
                          onClick={() => setSubmitType('fail')}
                        >
                          <span className="brokerage-segment__label">Fail</span>
                        </button>
                      </div>

                      <label className="profile-field">
                        <span className="profile-field__label">Days Trained</span>
                        <input
                          id="days"
                          className="profile-field__input"
                          type="number"
                          value={days}
                          onChange={e => setDays(parseInt(e.target.value!))}
                        />
                      </label>

                      <div className="home-card training-info-card">
                        <div className="training-info-card__label">Agreement</div>
                        <p className="training-info-card__body">{agreementText}</p>
                      </div>

                      <div className="auth-consent">
                        <label className="auth-consent__row">
                          <input
                            type="checkbox"
                            checked={agreed}
                            onChange={() => setAgreed(!agreed)}
                          />
                          <span>I have read and agree to the above statement</span>
                        </label>
                      </div>

                      <button
                        type="button"
                        id="western-button-submit"
                        className="profile-submit"
                        disabled={loading}
                        onClick={handleSubmit}
                      >
                        {loading ? 'Submitting…' : 'Submit'}
                      </button>
                    </>
                  ) : noTrainees ? (
                    <div className="scoreboard-empty">
                      <strong>Attention</strong>
                      <p>You don't have any trainees to release.</p>
                      <button
                        type="button"
                        className="home-action home-action--tint"
                        onClick={() => history.push('/newtrainee')}
                      >
                        <span className="home-action__icon">
                          <PlusIcon />
                        </span>
                        <span className="home-action__label">New Trainee</span>
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="home-section-label">Select Trainee Profile</div>
                      <div className="training-trainee-list">
                        {trainees.map(t => (
                          <div className="training-trainee-row" key={t.traineeCode}>
                            <button
                              type="button"
                              className="home-action"
                              onClick={() => getTraineeProfile(t.traineeCode)}
                            >
                              <span className="home-action__icon">
                                <SeatedIcon />
                              </span>
                              <span className="home-action__label">
                                {t.traineeCode}
                                <span className="training-trainee-row__meta">
                                  {t.released !== null ? `Released on: ${t.released}` : 'In Training'}
                                </span>
                              </span>
                              <ChevronRight />
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </IonContent>

            <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
              <div className="design-a">
                <div className="training-confirm-modal">
                  <div className="training-confirm-modal__title">Thank You</div>
                  <p className="training-confirm-modal__body">Your release has been submitted.</p>
                  <button
                    type="button"
                    className="profile-submit"
                    onClick={() => {
                        setShowModal(false);
                        history.push('/Home');
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </IonModal>
        </IonPage>
    );
};

export default ReleaseTrainee;
